import React from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Typography,
    IconButton,
    CircularProgress,
    useTheme,
    Button,
    Dialog,
    DialogContent,
    TextField,
    Stack,
    Chip,
    Tooltip,
    Fade,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { getStyles } from '../styles';
import { AI_ACTIONS } from './AIAssistantMenu';

// Get display title for action type
const getActionTitle = (actionType) => {
    const titles = {
        [AI_ACTIONS.SMART_REPLY]: 'Smart Reply',
        [AI_ACTIONS.APPOINTMENT_REMINDER]: 'Appointment Reminder',
        [AI_ACTIONS.FOLLOW_UP]: 'Follow-up Message',
        [AI_ACTIONS.HANDLE_CONCERN]: 'Response to Concern',
        [AI_ACTIONS.SUMMARIZE_CHAT]: 'Conversation Summary',
        [AI_ACTIONS.PAIN_POINTS]: 'Patient Pain Points',
        [AI_ACTIONS.ACTION_ITEMS]: 'Action Items',
        [AI_ACTIONS.EXPLAIN_TREATMENT]: 'Treatment Explanation',
        [AI_ACTIONS.PRICING_INFO]: 'Pricing Information',
        [AI_ACTIONS.IMPROVE_TONE]: 'Improved Message',
        [AI_ACTIONS.TRANSLATE]: 'Translation',
    };
    return titles[actionType] || 'AI Response';
};

// Check if action is a reply type (can be sent directly)
const isReplyAction = (actionType) => [
    AI_ACTIONS.SMART_REPLY,
    AI_ACTIONS.APPOINTMENT_REMINDER,
    AI_ACTIONS.FOLLOW_UP,
    AI_ACTIONS.HANDLE_CONCERN,
    AI_ACTIONS.EXPLAIN_TREATMENT,
    AI_ACTIONS.PRICING_INFO,
    AI_ACTIONS.IMPROVE_TONE,
    AI_ACTIONS.TRANSLATE,
].includes(actionType);

// WhatsApp message limits
const WHATSAPP_SOFT_LIMIT = 300; // Recommended for readability
const WHATSAPP_HARD_LIMIT = 4096; // WhatsApp's actual limit

const AIResultPanel = ({
    loading,
    result,
    error,
    actionType,
    onClose,
    onSend,
    onCopy,
    onRegenerate,
}) => {
    const theme = useTheme();
    const styles = getStyles(theme);
    const [copied, setCopied] = React.useState(false);
    const [editDialogOpen, setEditDialogOpen] = React.useState(false);
    const [editContent, setEditContent] = React.useState('');
    const [originalContent, setOriginalContent] = React.useState('');

    const handleCopy = () => {
        if (result?.content) {
            navigator.clipboard.writeText(result.content);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
            onCopy?.();
        }
    };

    const handleOpenEditDialog = () => {
        if (result?.content) {
            setEditContent(result.content);
            setOriginalContent(result.content);
            setEditDialogOpen(true);
        }
    };

    const handleCloseEditDialog = () => {
        setEditDialogOpen(false);
    };

    const handleResetContent = () => {
        setEditContent(originalContent);
    };

    const handleEditSend = () => {
        if (editContent?.trim()) {
            onSend?.(editContent);
            handleCloseEditDialog();
        }
    };

    const handleSend = () => {
        if (result?.content) {
            onSend?.(result.content);
        }
    };

    // Handle keyboard shortcuts in edit dialog
    const handleKeyDown = (e) => {
        // Cmd/Ctrl + Enter to send
        if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
            e.preventDefault();
            handleEditSend();
        }
        // Escape to close
        if (e.key === 'Escape') {
            handleCloseEditDialog();
        }
    };

    // Don't render if nothing to show
    if (!loading && !result && !error) {
        return null;
    }

    const isNotConfigured = result?.isNotConfigured;
    const canSend = !isNotConfigured && isReplyAction(actionType);
    const title = isNotConfigured ? 'AI Not Available' : getActionTitle(actionType);

    // Character count helpers for edit dialog
    const charCount = editContent?.length || 0;
    const isOverSoftLimit = charCount > WHATSAPP_SOFT_LIMIT;
    const isOverHardLimit = charCount > WHATSAPP_HARD_LIMIT;
    const hasChanges = editContent !== originalContent;

    // Info panel style for "not configured" state
    const infoPanelStyle = {
        backgroundColor: 'rgba(0, 184, 217, 0.08)',
        border: '1px solid rgba(0, 184, 217, 0.24)',
    };
    const infoTitleStyle = {
        color: '#006C9C',
    };

    return (
        <>
            <Box
                sx={{
                    ...styles.aiResultPanel,
                    ...(error ? styles.aiResultPanelError : {}),
                    ...(isNotConfigured ? infoPanelStyle : {}),
                }}
            >
                {/* Header */}
                <Box sx={styles.aiResultHeader}>
                    <Box sx={{
                        ...styles.aiResultTitle,
                        ...(error ? styles.aiResultTitleError : {}),
                        ...(isNotConfigured ? infoTitleStyle : {}),
                    }}>
                        <Icon
                            icon={(() => {
                                if (error) return 'mdi:alert-circle-outline';
                                if (isNotConfigured) return 'mdi:information-outline';
                                return 'mdi:sparkles';
                            })()}
                            width={16}
                            height={16}
                        />
                        <Typography component="span">
                            {error ? 'Error' : title}
                        </Typography>
                    </Box>
                    <IconButton
                        size="small"
                        onClick={onClose}
                        sx={styles.aiResultCloseBtn}
                    >
                        <Icon icon="mdi:close" width={14} height={14} />
                    </IconButton>
                </Box>

                {/* Content */}
                {(() => {
                    if (loading) {
                        return (
                            <Box sx={styles.aiLoadingContainer}>
                                <CircularProgress size={20} sx={{ color: '#00A76F' }} />
                                <Typography sx={styles.aiLoadingText}>
                                    Generating response...
                                </Typography>
                            </Box>
                        );
                    }
                    if (error) {
                        return (
                            <Typography sx={{ ...styles.aiResultContent, color: '#B71D18' }}>
                                {error}
                            </Typography>
                        );
                    }
                    return (
                        <Typography sx={{ ...styles.aiResultContent, ...(isNotConfigured ? { color: '#006C9C' } : {}) }}>
                            {result?.content}
                        </Typography>
                    );
                })()}

                {/* Actions - hide for "not configured" state */}
                {!loading && !isNotConfigured && (result || error) && (
                    <Box sx={styles.aiResultActions}>
                        {!error && (
                            <>
                                <Button
                                    sx={styles.aiResultActionBtn}
                                    onClick={handleCopy}
                                >
                                    <Icon
                                        icon={copied ? 'mdi:check' : 'mdi:content-copy'}
                                        width={14}
                                        height={14}
                                    />
                                    {copied ? 'Copied!' : 'Copy'}
                                </Button>

                                {canSend && (
                                    <>
                                        <Button
                                            sx={styles.aiResultActionBtn}
                                            onClick={handleOpenEditDialog}
                                        >
                                            <Icon icon="mdi:pencil-outline" width={14} height={14} />
                                            Edit
                                        </Button>

                                        <Button
                                            sx={{
                                                ...styles.aiResultActionBtn,
                                                ...styles.aiResultActionBtnPrimary,
                                            }}
                                            onClick={handleSend}
                                        >
                                            <Icon icon="mdi:send" width={14} height={14} />
                                            Send
                                        </Button>
                                    </>
                                )}
                            </>
                        )}

                        <Button
                            sx={styles.aiResultActionBtn}
                            onClick={onRegenerate}
                        >
                            <Icon icon="mdi:refresh" width={14} height={14} />
                            {error ? 'Retry' : 'Regenerate'}
                        </Button>
                    </Box>
                )}
            </Box>

            {/* Edit Dialog */}
            <Dialog
                open={editDialogOpen}
                onClose={handleCloseEditDialog}
                maxWidth="sm"
                fullWidth
                TransitionComponent={Fade}
                transitionDuration={200}
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        overflow: 'hidden',
                        boxShadow: '0 24px 48px rgba(0,0,0,0.2)',
                    }
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        background: 'linear-gradient(135deg, #00A76F 0%, #007867 100%)',
                        px: 2.5,
                        py: 2,
                    }}
                >
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                            <Box
                                sx={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: 2,
                                    bgcolor: 'rgba(255,255,255,0.2)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <Icon icon="mdi:pencil-outline" width={20} height={20} style={{ color: '#fff' }} />
                            </Box>
                            <Box>
                                <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: '1rem' }}>
                                    Edit Response
                                </Typography>
                                <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.75rem' }}>
                                    {title}
                                </Typography>
                            </Box>
                        </Stack>
                        <IconButton
                            onClick={handleCloseEditDialog}
                            sx={{ color: '#fff', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
                            size="small"
                        >
                            <Icon icon="mdi:close" width={20} height={20} />
                        </IconButton>
                    </Stack>
                </Box>

                <DialogContent sx={{ p: 0 }}>
                    {/* Editor */}
                    <Box sx={{ p: 2.5 }}>
                        <TextField
                            fullWidth
                            multiline
                            minRows={6}
                            maxRows={14}
                            value={editContent}
                            onChange={(e) => setEditContent(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="Edit your message..."
                            autoFocus
                            error={isOverHardLimit}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    fontSize: '0.9rem',
                                    lineHeight: 1.6,
                                    '&.Mui-focused': {
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: isOverHardLimit ? 'error.main' : '#00A76F',
                                            borderWidth: 2,
                                        }
                                    }
                                }
                            }}
                        />

                        {/* Character count & status bar */}
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{ mt: 1.5, px: 0.5 }}
                        >
                            <Stack direction="row" alignItems="center" spacing={1}>
                                {/* Character count chip */}
                                <Tooltip title={`WhatsApp limit: ${WHATSAPP_HARD_LIMIT} chars. Recommended: under ${WHATSAPP_SOFT_LIMIT} for readability.`}>
                                    <Chip
                                        size="small"
                                        icon={<Icon icon="mdi:text" width={14} height={14} />}
                                        label={`${charCount} / ${WHATSAPP_HARD_LIMIT}`}
                                        sx={{
                                            height: 24,
                                            fontSize: '0.7rem',
                                            bgcolor: (() => {
                                                if (isOverHardLimit) return 'error.lighter';
                                                if (isOverSoftLimit) return 'warning.lighter';
                                                return 'grey.100';
                                            })(),
                                            color: (() => {
                                                if (isOverHardLimit) return 'error.dark';
                                                if (isOverSoftLimit) return 'warning.dark';
                                                return 'text.secondary';
                                            })(),
                                            '& .MuiChip-icon': {
                                                color: 'inherit',
                                            }
                                        }}
                                    />
                                </Tooltip>

                                {/* Status indicators */}
                                {isOverSoftLimit && !isOverHardLimit && (
                                    <Typography variant="caption" sx={{ color: 'warning.main', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Icon icon="mdi:information-outline" width={14} height={14} />
                                        Long message
                                    </Typography>
                                )}
                                {isOverHardLimit && (
                                    <Typography variant="caption" sx={{ color: 'error.main', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Icon icon="mdi:alert-outline" width={14} height={14} />
                                        Exceeds limit
                                    </Typography>
                                )}
                                {hasChanges && !isOverHardLimit && !isOverSoftLimit && (
                                    <Typography variant="caption" sx={{ color: 'success.main', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Icon icon="mdi:pencil" width={14} height={14} />
                                        Modified
                                    </Typography>
                                )}
                            </Stack>

                            {/* Reset button */}
                            {hasChanges && (
                                <Tooltip title="Reset to original">
                                    <Button
                                        size="small"
                                        onClick={handleResetContent}
                                        sx={{
                                            minWidth: 'auto',
                                            px: 1,
                                            py: 0.5,
                                            fontSize: '0.75rem',
                                            color: 'text.secondary',
                                            '&:hover': { bgcolor: 'grey.100' }
                                        }}
                                        startIcon={<Icon icon="mdi:undo" width={14} height={14} />}
                                    >
                                        Reset
                                    </Button>
                                </Tooltip>
                            )}
                        </Stack>
                    </Box>

                    {/* Footer with actions */}
                    <Box
                        sx={{
                            px: 2.5,
                            py: 2,
                            bgcolor: 'grey.50',
                            borderTop: '1px solid',
                            borderColor: 'divider',
                        }}
                    >
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                            {/* Keyboard shortcut hint */}
                            <Typography variant="caption" sx={{ color: 'text.disabled', display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 0.5 }}>
                                <Box component="span" sx={{ bgcolor: 'grey.200', px: 0.75, py: 0.25, borderRadius: 0.5, fontFamily: 'monospace', fontSize: '0.65rem' }}>
                                    ⌘/Ctrl + Enter
                                </Box>
                                to send
                            </Typography>

                            {/* Action buttons */}
                            <Stack direction="row" spacing={1.5}>
                                <Button
                                    onClick={handleCloseEditDialog}
                                    sx={{
                                        color: 'text.secondary',
                                        '&:hover': { bgcolor: 'grey.200' }
                                    }}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    onClick={handleEditSend}
                                    variant="contained"
                                    disabled={!editContent?.trim() || isOverHardLimit}
                                    startIcon={<Icon icon="mdi:send" width={18} height={18} />}
                                    sx={{
                                        px: 3,
                                        borderRadius: 2,
                                        background: 'linear-gradient(135deg, #00A76F 0%, #007867 100%)',
                                        boxShadow: '0 4px 12px rgba(0, 167, 111, 0.3)',
                                        '&:hover': {
                                            background: 'linear-gradient(135deg, #007867 0%, #00A76F 100%)',
                                            boxShadow: '0 6px 16px rgba(0, 167, 111, 0.4)',
                                        },
                                        '&.Mui-disabled': {
                                            background: 'grey.300',
                                            boxShadow: 'none',
                                        }
                                    }}
                                >
                                    Send Message
                                </Button>
                            </Stack>
                        </Stack>
                    </Box>
                </DialogContent>
            </Dialog>
        </>
    );
};

AIResultPanel.propTypes = {
    loading: PropTypes.bool,
    result: PropTypes.shape({
        content: PropTypes.string,
        actionType: PropTypes.string,
        isNotConfigured: PropTypes.bool,
    }),
    error: PropTypes.string,
    actionType: PropTypes.string,
    onClose: PropTypes.func.isRequired,
    onSend: PropTypes.func,
    onCopy: PropTypes.func,
    onRegenerate: PropTypes.func,
};

AIResultPanel.defaultProps = {
    loading: false,
    result: null,
    error: null,
    actionType: null,
    onSend: null,
    onCopy: null,
    onRegenerate: null,
};

export default AIResultPanel;
